import { FC, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertColor, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import strings from 'common/Translation/Translate';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../../utils/axios';
import { PageProps } from '../../../utils/constants/data';
import { URLS } from '../../../utils/constants/urls';
import {
  filterData,
  filterDataforApi,
  pageNo,
  rowCount,
  setFilter,
  setFilterDataForApi,
  setPageNo,
} from '../../../utils/redux/reducer/filter-slice';
import ListPage from '../CommonPage';
import { PageConfigModel } from '../CommonPage/ListPage';
import StyledTabs from 'components/StyledTabs';
import Notification from 'components/Notification';
import { getTimePeriodOptions } from 'utils/validators/HelperFunctions';
import { routes } from '../../../utils/constants/routes';
import { useNavigate } from 'react-router';
import {
  TaskAssigneeCell,
  TaskDueDateCell,
  TaskPriorityIcon,
  TaskStatusCell,
  TaskTitleCell,
} from './taskListDisplay';

const filterSelectSx = {
  height: 36,
  fontSize: 13,
  bgcolor: '#fff',
  '& .MuiSelect-select': { py: 0.75 },
};

const PRIORITY_OPTIONS = [
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
];

interface TaskRecordsListProps {
  tabValue: number;
  onTabChange: (value: number) => void;
  assignees: { label: string; value: string | number }[];
}

const TaskRecordsList: FC<TaskRecordsListProps> = ({ tabValue, onTabChange, assignees }) => {
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const currentFilterData = useSelector(filterData);
  const pageSize = useSelector(rowCount);
  const [pageResponse, setPageResponse] = useState<any>({ content: [], totalElements: 0 });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState<string | number>('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const navigateToSource = useCallback(
    (item: any) => {
      switch (item.type) {
        case 'RFQ':
          Navigate(routes.viewRfq + '/' + item.sourceId);
          break;
        case 'BOOKING_LINE_ITEM':
          Navigate(routes.bookings + '/' + item.sourceId);
          break;
        case 'SALES_CALL':
          Navigate(routes.editSalesManagement + '/' + item.sourceId);
          break;
        default:
          break;
      }
    },
    [Navigate],
  );

  const applyHeaderFilter = useCallback(
    (updatedFilter: Record<string, any>) => {
      const filterArray: string[] = [];
      Object.keys(updatedFilter).forEach(key => {
        const value = updatedFilter[key];
        if (value && value !== '-') {
          if (filterArray.length > 0) {
            filterArray.push('and');
          }
          filterArray.push(`${key}:cic:${value}`);
        }
      });
      dispatch(setFilter(updatedFilter));
      dispatch(setFilterDataForApi(filterArray));
      dispatch(setPageNo(0));
    },
    [dispatch],
  );

  const handleTimePeriodChange = useCallback(
    (value: string) => {
      setSelectedTimePeriod(value);
      applyHeaderFilter({
        ...currentFilterData,
        timePeriod: value,
      });
    },
    [applyHeaderFilter, currentFilterData],
  );

  const handleAssigneeFilterChange = useCallback(
    (value: string | number) => {
      setSelectedAssignee(value);
      applyHeaderFilter({
        ...currentFilterData,
        assignee: value || '',
      });
    },
    [applyHeaderFilter, currentFilterData],
  );

  const handlePriorityChange = useCallback(
    (value: string) => {
      setSelectedPriority(value);
      applyHeaderFilter({
        ...currentFilterData,
        priority: value,
      });
    },
    [applyHeaderFilter, currentFilterData],
  );

  const handleClearAllHeaderFilters = useCallback(() => {
    setSelectedTimePeriod('');
    setSelectedAssignee('');
    setSelectedPriority('');
    applyHeaderFilter({
      ...(currentFilterData?.search ? { search: currentFilterData.search } : {}),
    });
  }, [applyHeaderFilter, currentFilterData]);

  const getList = useCallback(
    async (filterData?: PageProps) => {
      const body: PageProps = {
        page: index + 1,
        size: pageSize,
        filter: filter,
        sort: ['createdAt:desc'],
      };
      const url = tabValue === 1 ? `${URLS.taskListPage}?showTeamTasks=true` : URLS.taskListPage;
      try {
        setIsLoading(true);
        const { data } = await axiosInstance.post(url, filterData ?? body);
        setPageResponse(data);
      } catch (error: any) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [index, pageSize, filter, tabValue],
  );

  useEffect(() => {
    getList();
  }, [getList]);

  const changeAssignee = useCallback(
    async (id: number, assigneeId: number) => {
      try {
        setIsLoading(true);
        await axiosInstance.put(URLS.assignTasks(id, assigneeId));
        await getList();
      } catch (error: any) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [getList],
  );

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newValue: number) => {
      if (newValue !== 1 && selectedAssignee) {
        setSelectedAssignee('');
        const { assignee, ...rest } = currentFilterData || {};
        applyHeaderFilter(rest);
      }
      onTabChange(newValue);
    },
    [onTabChange, applyHeaderFilter, currentFilterData, selectedAssignee],
  );

  const pageConfig: PageConfigModel = useMemo(
    () => ({
      title: strings.taskList,
      entityLabel: strings.taskList,
      subtitle: strings.task_list_subtitle,
      searchField: 'search',
      searchPlaceholder: 'Search by title',
      hideActionText: true,
      customComponent: () => (
        <StyledTabs
          compact
          align="right"
          tabs={[{ label: strings.myTasks || 'My Tasks' }, { label: strings.myTeamTasks || 'My Team Tasks' }]}
          value={tabValue}
          onChange={handleTabChange}
          aria-label="task list tabs"
          data-testId={'tab'}
        />
      ),
      headerFilters: () => (
        <>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: 12 }}>Time period</InputLabel>
            <Select
              label="Time period"
              value={selectedTimePeriod || ''}
              onChange={event => handleTimePeriodChange(String(event.target.value))}
              sx={filterSelectSx}>
              <MenuItem value="">All time</MenuItem>
              {getTimePeriodOptions()
                .filter((option: any) => option.value !== '-')
                .map((option: any) => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {tabValue === 1 ? (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontSize: 12 }}>{strings.bookingList_assignee || 'Assignee'}</InputLabel>
              <Select
                label={strings.bookingList_assignee || 'Assignee'}
                value={selectedAssignee || ''}
                onChange={event => handleAssigneeFilterChange(event.target.value)}
                sx={filterSelectSx}>
                <MenuItem value="">All assignees</MenuItem>
                {assignees.map(item => (
                  <MenuItem key={item.value} value={item.value} sx={{ fontSize: 13 }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 12 }}>{strings.tasklist_priority}</InputLabel>
            <Select
              label={strings.tasklist_priority}
              value={selectedPriority || ''}
              onChange={event => handlePriorityChange(String(event.target.value))}
              sx={filterSelectSx}>
              <MenuItem value="">All priorities</MenuItem>
              {PRIORITY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ),
      filterChips: [
        ...(selectedTimePeriod
          ? [
              {
                key: 'timePeriod',
                label: `Time period : ${
                  getTimePeriodOptions().find((option: any) => option.value === selectedTimePeriod)?.label ||
                  selectedTimePeriod
                }`,
              },
            ]
          : []),
        ...(tabValue === 1 && selectedAssignee
          ? [
              {
                key: 'assignee',
                label: `Assignee : ${
                  assignees.find(item => String(item.value) === String(selectedAssignee))?.label || selectedAssignee
                }`,
              },
            ]
          : []),
        ...(selectedPriority
          ? [
              {
                key: 'priority',
                label: `Priority : ${
                  PRIORITY_OPTIONS.find(option => option.value === selectedPriority)?.label || selectedPriority
                }`,
              },
            ]
          : []),
      ],
      onClearFilterChip: (key: string) => {
        if (key === 'timePeriod') handleTimePeriodChange('');
        if (key === 'assignee') handleAssigneeFilterChange('');
        if (key === 'priority') handlePriorityChange('');
      },
      onClearAllFilterChips: handleClearAllHeaderFilters,
      tableColumn: [
        {
          field: 'title',
          headerName: strings.tasklist_title,
          flex: 2.6,
          sortable: true,
          renderCell: (value: any, row: any) => (
            <TaskTitleCell title={value} description={row.description} onClick={() => navigateToSource(row)} />
          ),
        },
        {
          field: 'status',
          headerName: strings.lead_management_contact_details_status,
          flex: 1.05,
          sortable: true,
          renderCell: (_value: any, row: any) => (
            <TaskStatusCell id={row.id} status={row.status} onSuccess={() => getList()} />
          ),
        },
        {
          field: 'priority',
          headerName: strings.tasklist_priority,
          flex: 0.55,
          sortable: true,
          renderCell: (value: any) => <TaskPriorityIcon priority={value} />,
        },
        {
          field: 'dueDate',
          headerName: strings.tasklist_due_date,
          flex: 1.05,
          sortable: true,
          renderCell: (value: any, row: any) => <TaskDueDateCell dueDate={value} status={row.status} />,
        },
        ...(tabValue === 1
          ? [
              {
                field: 'assignedTo',
                headerName: strings.bookingList_assignee || 'Assignee',
                flex: 0.55,
                sortable: false,
                renderCell: (_value: any, row: any) => (
                  <TaskAssigneeCell row={row} assignees={assignees} onAssign={changeAssignee} />
                ),
              },
            ]
          : []),
      ],
    }),
    [
      tabValue,
      handleTabChange,
      selectedTimePeriod,
      handleTimePeriodChange,
      selectedAssignee,
      handleAssigneeFilterChange,
      selectedPriority,
      handlePriorityChange,
      assignees,
      handleClearAllHeaderFilters,
      navigateToSource,
      getList,
      changeAssignee,
    ],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage isLoading={isLoading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
    </>
  );
};

export default TaskRecordsList;
